import { useAuth0 } from "@auth0/auth0-react";
import { useQuery } from "@tanstack/react-query";
import { fuelPlanComplianceOptions } from "./api";
import type { TractorCompliance } from "./api";
import { summarizeTractorCompliance } from "./services";
import classNames from "classnames";
import { useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import { AiFillCaretDown, AiTwotoneCalendar } from "react-icons/ai";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import TractorFuelStopCompliance from "./tractor-fuel-stop-compliance";
import CsvDownloadButton from "react-json-to-csv";
import ColumnFilter from "components/column-filter";

function TractorComplianceTable() {
  const { user } = useAuth0();
  const [selectedRowData, setSelectedRowData] = useState<TractorCompliance | null>(null);
  const [startDate, setStartDate] = useState(new Date(new Date().getTime() - 30 * 24 * 60 * 60 * 1000));
  const [endDate, setEndDate] = useState(new Date(new Date().getTime() - 2 * 24 * 60 * 60 * 1000));

  // The date range is part of the query key, so changing either date refetches.
  const { data: customerComplianceData, isFetching } = useQuery(
    fuelPlanComplianceOptions(
      user!["https://ifuelsmart.com/company"],
      startDate.toISOString().substring(0, 10),
      endDate.toISOString().substring(0, 10)
    )
  );
  const customerComplianceDataLoading = isFetching || !customerComplianceData;

  const complianceForExport = useMemo(() => {
    if (!customerComplianceData) return [];
    return customerComplianceData.tractorFuelStops.map(summarizeTractorCompliance);
  }, [customerComplianceData]);

  const columns = useMemo<ColumnDef<TractorCompliance>[]>(
    () => [
      {
        header: "Tractor",
        accessorKey: "tractor",
        // debounceMs 0 preserves the pre-v8 filter-as-you-type behavior; the
        // shared ColumnFilter otherwise defaults to a 1000ms debounce.
        meta: { filterComponent: ColumnFilter, filterDebounceMs: 0, filterPlaceholder: "Search Tractor" },
      },
      {
        header: "Total Fuel Stops",
        id: "totalFuelStops",
        accessorFn: (row) => summarizeTractorCompliance(row).numberOfFuelStops,
        enableColumnFilter: false,
        cell: ({ row, getValue }) => (
          <div
            className="cursor-pointer underline"
            onClick={() => {
              setSelectedRowData(row.original);
            }}
          >
            {getValue<number>()}
          </div>
        ),
      },
      {
        header: "Compliant Fuel Stops",
        id: "compliantFuelStops",
        accessorFn: (row) => summarizeTractorCompliance(row).numberOfCompliantFuelStops,
        enableColumnFilter: false,
      },
      {
        header: "Non Compliant Fuel Stops",
        id: "nonCompliantFuelStops",
        accessorFn: (row) => summarizeTractorCompliance(row).numberOfNonCompliantFuelStops,
        enableColumnFilter: false,
      },
      {
        header: "Compliance",
        id: "Compliance",
        accessorFn: (row) => summarizeTractorCompliance(row).compliancePercent,
        enableColumnFilter: false,
        cell: ({ getValue }) => {
          const value = getValue<number>();
          return (
            <div
              className={classNames("px-2 py-1 text-center rounded w-14", {
                "bg-green-300": value >= 80,
                "bg-yellow-300": value >= 60 && value < 80,
                "bg-red-300": value < 60,
              })}
            >
              {value}%
            </div>
          );
        },
      },
    ],
    []
  );

  const tableData = useMemo(() => customerComplianceData?.tractorFuelStops ?? [], [customerComplianceData]);

  const table = useReactTable({
    data: tableData,
    columns,
    initialState: {
      sorting: [{ id: "Compliance", desc: true }],
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <>
      <div className="h-full flex flex-col p-4">
        {selectedRowData && (
          <TractorFuelStopCompliance onClose={() => setSelectedRowData(null)} data={selectedRowData} />
        )}

        <div className="flex items-center mb-4 h-14">
          <div className="relative mr-4">
            <label className="block font-medium mb-1 text-sm" htmlFor="datepicker">
              Start Date
            </label>
            <DatePicker
              selected={startDate}
              onChange={(date) => date && setStartDate(date)}
              dateFormat="MM/dd/yyyy"
              id="datepicker"
              name="datepicker"
              className="bg-white rounded shadow py-2 px-3"
            />
            <div className="absolute top-9 right-4">
              <AiTwotoneCalendar className="text-gray-400" />
            </div>
          </div>
          <div className="relative">
            <label className="block font-medium mb-1 text-sm" htmlFor="datepicker">
              End Date
            </label>
            <DatePicker
              selected={endDate}
              onChange={(date) => date && setEndDate(date)}
              dateFormat="MM/dd/yyyy"
              id="datepicker"
              name="datepicker"
              className="bg-white rounded shadow py-2 px-3"
            />
            <div className="absolute top-9 right-4">
              <AiTwotoneCalendar className="text-gray-400" />
            </div>
          </div>

          <div className="flex ml-auto space-x-2">
            <CsvDownloadButton
              disabled={customerComplianceDataLoading}
              className="bg-green-600 text-white px-2 rounded hover:bg-green-500 transition-colors disabled:bg-gray-300 disabled:text-gray-400 disabled:cursor-not-allowed"
              data={complianceForExport}
              headers={[
                "Tractor",
                "Number of Fuel Stops",
                "Number of Compliant Fuel Stops",
                "Number of Non Compliant Fuel Stops",
                "Compliance Percent",
              ]}
              delimiter=","
            >
              Export Compliance
            </CsvDownloadButton>
            {customerComplianceDataLoading ? (
              <div
                className={classNames("ml-auto inline-flex flex-col items-center bg-slate-300 rounded px-3 py-2", {})}
              >
                <div className="text-xs font-medium mb-1">Avg. Compliance</div>
                <svg
                  className="animate-spin h-5 w-5"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              </div>
            ) : (
              <div
                className={classNames("ml-auto inline-flex flex-col items-center bg-green-300 rounded px-3 py-2", {
                  "bg-green-300": customerComplianceData!.averageCompliancePercent >= 80,
                  "bg-yellow-300":
                    customerComplianceData!.averageCompliancePercent >= 60 &&
                    customerComplianceData!.averageCompliancePercent < 80,
                  "bg-red-300": customerComplianceData!.averageCompliancePercent < 60,
                })}
              >
                <div className="text-xs font-medium mb-1">Avg. Compliance</div>
                <div className="text-sm font-bold">{Math.round(customerComplianceData!.averageCompliancePercent)}%</div>
              </div>
            )}
          </div>
        </div>

        <div className="flex-1 relative">
          <div className="absolute inset-0 overflow-auto">
            <div className="min-w-full">
              <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0">
                    {table.getHeaderGroups().map((headerGroup) => (
                      <tr key={headerGroup.id}>
                        {headerGroup.headers.map((header) => {
                          const column = header.column;
                          const sorted = column.getIsSorted();
                          const Filter = column.columnDef.meta?.filterComponent;
                          const handleSortClick = () => {
                            if (!column.getCanSort()) return;
                            if (!sorted) {
                              column.toggleSorting(false, false);
                            } else {
                              column.toggleSorting(sorted === "asc", false);
                            }
                          };
                          return (
                            <th
                              key={header.id}
                              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 whitespace-nowrap"
                              onClick={handleSortClick}
                            >
                              {flexRender(column.columnDef.header, header.getContext())}
                              <span>
                                {sorted ? (
                                  sorted === "desc" ? (
                                    <AiFillCaretDown className="inline-block text-base" />
                                  ) : (
                                    <AiFillCaretDown className="inline-block text-base rotate-180" />
                                  )
                                ) : (
                                  ""
                                )}
                              </span>
                              {Filter ? <Filter column={column} /> : null}
                            </th>
                          );
                        })}
                      </tr>
                    ))}
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {customerComplianceDataLoading && (
                      <tr>
                        <td colSpan={5} className="py-4">
                          <svg
                            className="animate-spin h-7 w-7 mx-auto"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                        </td>
                      </tr>
                    )}
                    {!customerComplianceDataLoading &&
                      table.getRowModel().rows.map((row) => (
                        <tr key={row.id}>
                          {row.getVisibleCells().map((cell) => (
                            <td key={cell.id} className="px-6 py-4 whitespace-nowrap">
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                          ))}
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default TractorComplianceTable;
